import { createStore, createEffect, createEvent, combine, sample } from 'effector';
import i18n from 'i18n';
import { $notificationStore } from '../notificationStore';
import { ApiUtils } from '@shared/utils';
import { AccountsApi, UsersApi } from '@shared/api/rest';
import type { AccountRequest, AccountResponse } from '@shared/api/rest/accounts';
import type { AddUserAccountsRequest, DeleteUserAccountsRequest } from '@shared/api/rest/users';
import { type IQueryParams, RequestStrategyEnum } from '@shared/api/models';

const getUserAccountsEffect = createEffect((params: IQueryParams<AccountRequest>) => AccountsApi.getAccounts(params.request, params?.signal));

const addAccountsEffect = createEffect(async (params: IQueryParams<AddUserAccountsRequest>) => {
  const user = await UsersApi.addUserAccounts(params.request, params.signal);
  if (!user.success) throw new Error(user.message || 'Error adding account');
  return { AssignedUserId: params.request.userIds };
});

const deleteAccountEffect = createEffect(async (params: IQueryParams<DeleteUserAccountsRequest>) => {
  const user = await UsersApi.deleteUserAccounts(params.request, params.signal);
  if (!user.success) throw new Error(user.message || 'Error deleting account');
  return { AssignedUserId: params.request.userIds };
});
const clearEvent = createEvent();
sample({
  source: addAccountsEffect.doneData,
  target: getUserAccountsEffect,
  fn: (doneData) => {
    return {
      request: { AssignedUserId: doneData.AssignedUserId },
    };
  },
});

sample({
  source: deleteAccountEffect.doneData,
  target: getUserAccountsEffect,
  fn: (doneData) => {
    return {
      request: { AssignedUserId: doneData.AssignedUserId },
    };
  },
});

const $dataStore = createStore<AccountResponse[]>([])
  .on(getUserAccountsEffect.done, (current, data) => data.result.data || current)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getUserAccountsEffect.pending, (_, data) => data)
  .on(getUserAccountsEffect.fail, () => false)
  .on(getUserAccountsEffect.done, () => false)
  .on(addAccountsEffect.pending, (_, data) => data)
  .on(addAccountsEffect.fail, () => false)
  .on(deleteAccountEffect.pending, (_, data) => data)
  .on(deleteAccountEffect.fail, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getUserAccountsEffect, () => null)
  .on(getUserAccountsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getUserAccountsEffect.done, () => null)
  .on(addAccountsEffect, () => null)
  .on(addAccountsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(addAccountsEffect.done, () => null)
  .on(deleteAccountEffect, () => null)
  .on(deleteAccountEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(deleteAccountEffect.done, () => null)
  .reset(clearEvent);
$errorStore.watch((x) => ApiUtils.errorStoreActionForbiddenWatcher(x, $notificationStore.events.setError));

export const $userAccountsStore = {
  events: {
    getUserAccounts: ApiUtils.wrapperApi(getUserAccountsEffect, RequestStrategyEnum.Debounce),
    addAccounts: ApiUtils.wrapperApi(addAccountsEffect, RequestStrategyEnum.Debounce),
    deleteAccount: ApiUtils.wrapperApi(deleteAccountEffect, RequestStrategyEnum.Debounce),
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    loading: $loadingStore,
    error: $errorStore,
  }),
};
